import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import RFB from '@novnc/novnc';
import {
  Monitor, RefreshCw, Maximize2, Loader2, AlertTriangle, Power, Plus, Play, Trash2, User, Users,
} from 'lucide-react';

// Browser-embedded PowerFLOW desktops. Each engineer gets their own TigerVNC
// session on the Master (localhost-bound, reached only through a per-session SSH
// tunnel + websockify token), so PowerVIZ / PowerCASE / PowerInsight run in the
// browser with no VNC client. Sessions are managed here; the login gates access.
const GEOMS = ['1920x1080', '2560x1440', '1600x1000', '1366x768'];

export default function Desktop() {
  const [sessions, setSessions] = useState([]);
  const [you, setYou] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [geom, setGeom] = useState('1920x1080');
  const [name, setName] = useState('');
  const [conn, setConn] = useState(null);     // {display} when viewing a desktop
  const [status, setStatus] = useState('idle');
  const [statusMsg, setStatusMsg] = useState('');
  const [scale, setScale] = useState(true);
  const screenRef = useRef(null);
  const rfbRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get('/api/vnc/sessions');
      if (r.data?.success) { setSessions(r.data.sessions || []); setYou(r.data.you); setErr(null); }
      else setErr(r.data?.error || 'failed to list sessions');
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const disconnect = useCallback(() => {
    if (rfbRef.current) { try { rfbRef.current.disconnect(); } catch { /* */ } rfbRef.current = null; }
  }, []);

  const connectWithToken = useCallback((display, token) => {
    setConn({ display }); setStatus('connecting'); setStatusMsg('');
    // let the screen div mount first
    setTimeout(() => {
      disconnect();
      const url = `${window.location.origin.replace(/^http/, 'ws')}/vnc/websockify?token=${encodeURIComponent(token)}`;
      let rfb;
      try { rfb = new RFB(screenRef.current, url, { wsProtocols: ['binary'] }); }
      catch (e) { setStatus('error'); setStatusMsg(String(e?.message || e)); return; }
      rfb.scaleViewport = scale;
      rfb.resizeSession = false;
      rfb.background = '#0b1220';
      rfb.addEventListener('connect', () => setStatus('connected'));
      rfb.addEventListener('disconnect', (e) => {
        setStatus('disconnected');
        setStatusMsg(e?.detail?.clean ? 'Session closed.' : 'Connection lost.');
      });
      rfb.addEventListener('securityfailure', (e) => { setStatus('error'); setStatusMsg(e?.detail?.reason || 'VNC security failure'); });
      rfb.addEventListener('credentialsrequired', () => {
        const pw = window.prompt('VNC session password (the one you use in RealVNC/TurboVNC):');
        if (pw != null) rfb.sendCredentials({ password: pw });
        else { rfb.disconnect(); }
      });
      rfbRef.current = rfb;
    }, 30);
  }, [disconnect, scale]);

  useEffect(() => () => disconnect(), [disconnect]);   // teardown on unmount
  useEffect(() => { if (rfbRef.current) rfbRef.current.scaleViewport = scale; }, [scale]);

  const startNew = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await axios.post('/api/vnc/session', { geometry: geom, name: name.trim() });
      if (r.data?.success) { setName(''); connectWithToken(r.data.display, r.data.token); }
      else setErr(r.data?.error || 'failed to start');
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  const attach = async (display) => {
    setBusy(true); setErr(null);
    try {
      const r = await axios.post(`/api/vnc/session/${display}/attach`);
      if (r.data?.success) connectWithToken(display, r.data.token);
      else setErr(r.data?.error || 'failed to connect');
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  const stop = async (display) => {
    if (!window.confirm(`Stop desktop :${display}? Any unsaved GUI work in it will be lost.`)) return;
    setBusy(true);
    try { await axios.post(`/api/vnc/session/${display}/stop`); await load(); }
    catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  const backToList = () => { disconnect(); setConn(null); setStatus('idle'); load(); };
  const fullscreen = () => { screenRef.current?.requestFullscreen?.().catch(() => {}); };

  // ---- Connected view ----
  if (conn) {
    const dot = { connected: '#22c55e', connecting: '#eab308', disconnected: '#f59e0b', error: '#ef4444' }[status] || '#64748b';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 150px)', minHeight: 460, gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={backToList} style={btn('#334155', '#cbd5e1')}>← Sessions</button>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Monitor style={{ width: 16, height: 16, color: '#22d3ee' }} />
            <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>Desktop :{conn.display}</span>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot }} />
            <span style={{ color: '#9ca3af', fontSize: 12, textTransform: 'capitalize' }}>{status}</span>
          </span>
          <div style={{ flex: 1 }} />
          <label style={{ color: '#9ca3af', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={scale} onChange={(e) => setScale(e.target.checked)} /> Fit
          </label>
          <button onClick={fullscreen} style={btn('#334155', '#cbd5e1')}><Maximize2 style={ic} /> Fullscreen</button>
          <button onClick={backToList} style={btn('rgba(248,113,113,0.4)', '#f87171')}><Power style={ic} /> Disconnect</button>
        </div>
        <div style={{ position: 'relative', flex: 1, minHeight: 0, background: '#0b1220', border: '1px solid #1e293b', borderRadius: 12, overflow: 'hidden' }}>
          {/* Right-click must go to the remote desktop, never the browser's own menu */}
          <div ref={screenRef} onContextMenu={(e) => e.preventDefault()} style={{ width: '100%', height: '100%' }} />
          {status !== 'connected' && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
              <div style={{ textAlign: 'center' }}>
                {status === 'connecting' && <><Loader2 style={{ width: 26, height: 26, color: '#22d3ee', animation: 'spin 1s linear infinite' }} /><p>Connecting…</p></>}
                {status === 'error' && <><AlertTriangle style={{ width: 26, height: 26, color: '#ef4444' }} /><p style={{ color: '#fca5a5' }}>{statusMsg}</p></>}
                {status === 'disconnected' && <p>{statusMsg || 'Disconnected.'}</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- Session list ----
  const mine = sessions.filter((s) => s.mine);
  const others = sessions.filter((s) => !s.mine);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 820 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Monitor style={{ width: 20, height: 20, color: '#22d3ee' }} />
        <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 700, margin: 0 }}>Remote Desktops</h2>
        <span style={{ color: '#64748b', fontSize: 12 }}>on the Master · GPU-accelerated (Tesla T4)</span>
        <div style={{ flex: 1 }} />
        <button onClick={load} title="Refresh" style={btn('#334155', '#94a3b8')}><RefreshCw style={ic} /></button>
      </div>

      {err && <div style={{ color: '#fca5a5', fontSize: 13 }}>{err}</div>}

      {/* Start a new one */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Plus style={{ width: 18, height: 18, color: '#22c55e' }} />
        <span style={{ color: '#e5e7eb', fontSize: 14, fontWeight: 600 }}>New desktop session</span>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40}
          onKeyDown={(e) => { if (e.key === 'Enter' && !busy) startNew(); }}
          placeholder="Name (e.g. your name or case)"
          style={{ flex: '1 1 200px', minWidth: 160, background: '#0b1220', color: '#e5e7eb', border: '1px solid #334155', borderRadius: 6, padding: '6px 10px', fontSize: 12 }} />
        <label style={{ color: '#9ca3af', fontSize: 12 }}>
          Resolution{' '}
          <select value={geom} onChange={(e) => setGeom(e.target.value)}
            style={{ background: '#0b1220', color: '#e5e7eb', border: '1px solid #334155', borderRadius: 6, padding: '5px 8px', fontSize: 12 }}>
            {GEOMS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </label>
        <button onClick={startNew} disabled={busy}
          style={{ ...btn('rgba(34,197,94,0.5)', '#22c55e'), background: 'rgba(34,197,94,0.12)' }}>
          {busy ? <Loader2 style={{ ...ic, animation: 'spin 1s linear infinite' }} /> : <Play style={ic} />} Start &amp; connect
        </button>
      </div>

      {loading ? (
        <div style={{ color: '#6b7280', fontSize: 13 }}><Loader2 style={{ ...ic, animation: 'spin 1s linear infinite' }} /> loading sessions…</div>
      ) : (
        <>
          <Section title="Your session" icon={<User style={{ width: 14, height: 14, color: '#22d3ee' }} />}
            empty="You don't have a session yet — start one above." rows={mine} you={you}
            onConnect={attach} onStop={stop} busy={busy} />
          <Section title="Other sessions" icon={<Users style={{ width: 14, height: 14, color: '#a78bfa' }} />}
            empty="No other active sessions." rows={others} you={you}
            onConnect={attach} onStop={stop} busy={busy} sharedNote />
        </>
      )}

      <p style={{ color: '#64748b', fontSize: 11, margin: 0 }}>
        Sessions run on the Master and are reachable only through this dashboard (each is bound to localhost and tunneled).
        You'll be asked for the VNC password on connect. Inside the desktop, launch 3D apps with <code style={{ color: '#93c5fd' }}>vglrun powerviz</code> for GPU acceleration.
      </p>
    </div>
  );
}

function Section({ title, icon, empty, rows, you, onConnect, onStop, busy, sharedNote }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
        {icon}<span style={{ color: '#9ca3af', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</span>
      </div>
      {rows.length === 0 ? (
        <div style={{ color: '#4b5563', fontSize: 12, padding: '4px 2px' }}>{empty}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((s) => (
            <div key={s.display} style={{ ...card, display: 'flex', alignItems: 'center', gap: 12 }}>
              <Monitor style={{ width: 16, height: 16, color: '#64748b', flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {s.name || `Desktop :${s.display}`}
                  {s.name && <span style={{ color: '#64748b', fontSize: 11, fontWeight: 400 }}>:{s.display}</span>}
                </div>
                <div style={{ color: '#64748b', fontSize: 11 }}>
                  {s.owner ? `owner ${s.owner}` : (s.source === 'existing' ? 'pre-existing (shared)' : 'unclaimed')}
                  {s.geometry ? ` · ${s.geometry}` : ''}
                </div>
              </div>
              <div style={{ flex: 1 }} />
              <button onClick={() => onConnect(s.display)} disabled={busy}
                style={{ ...btn('rgba(34,211,238,0.5)', '#22d3ee'), background: 'rgba(34,211,238,0.12)' }}>
                <Play style={ic} /> Connect
              </button>
              {s.display >= 10 && (
                <button onClick={() => onStop(s.display)} disabled={busy} title="Stop session"
                  style={btn('rgba(248,113,113,0.4)', '#f87171')}><Trash2 style={ic} /></button>
              )}
            </div>
          ))}
        </div>
      )}
      {sharedNote && rows.length > 0 && (
        <p style={{ color: '#64748b', fontSize: 10, marginTop: 6 }}>Note: connecting to a shared desktop means you'll see and control the same screen as whoever else is on it.</p>
      )}
    </div>
  );
}

const card = { background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, padding: '12px 16px' };
const ic = { width: 14, height: 14 };
const btn = (border, color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8,
  border: `1px solid ${border}`, background: 'transparent', color, fontSize: 12, fontWeight: 600, cursor: 'pointer',
});
